import './EditorPanel.css';

import { FC, useEffect, useState } from 'react';
import {
  AccordionGroupCustomEvent,
  IonAccordion,
  IonAccordionGroup,
} from '@ionic/react';
import EditorCanvasForm from '../EditorCanvasForm/EditorCanvasForm';
import EditorObjectForm from '../EditorObjectForm/EditorObjectForm';
import ResizablePanel from '../ResizablePanel/ResizablePanel';
import { useLayoutEditorContext } from '../../../../../hooks/context/useLayoutEditorContext';
import { useFullscreenReferenceNavigation } from '../../hooks/useFullscreenReferenceNavigation';
import { useSaveErrorFocus } from '../../hooks/useSaveErrorFocus';
import { FullscreenReferenceType } from '../../../../../types/types';

const EditorPanel: FC = () => {
  const [openSections, setOpenSections] = useState<string[]>([
    'object',
    'canvas',
  ]);
  const { definition, selectedIndex } = useLayoutEditorContext();
  const { openFullscreenReferenceRoute } = useFullscreenReferenceNavigation();

  useSaveErrorFocus({
    onOpenSection: (section): void => {
      setOpenSections((current) =>
        current.includes(section) ? current : [...current, section]
      );
    },
  });

  // Manual open/close of the panel sections; reacts only to the group event
  // (inner ion-accordion events bubble through with a different target).
  const handleAccordionToggle = (event: AccordionGroupCustomEvent): void => {
    if (event.target.tagName === 'ION-ACCORDION-GROUP') {
      setOpenSections((event.detail.value as string[]) ?? []);
    }
  };

  const handleOpenObjectFullscreenReference = (
    reference: FullscreenReferenceType
  ): void => openFullscreenReferenceRoute(reference, 'object');

  const handleOpenCanvasFullscreenReference = (
    reference: FullscreenReferenceType
  ): void => openFullscreenReferenceRoute(reference, 'canvas');

  // The object section follows the selection: opens when an object gets selected,
  // closes when the selection is cleared. Manual toggling stays possible in between.
  const hasSelection = selectedIndex !== null;
  useEffect(() => {
    setOpenSections((current) => {
      const withoutObject = current.filter((section) => section !== 'object');
      return hasSelection ? [...withoutObject, 'object'] : withoutObject;
    });
  }, [hasSelection]);

  if (!definition) {
    return null;
  }

  return (
    <>
      <ResizablePanel>
        <IonAccordionGroup
          multiple
          value={openSections}
          onIonChange={handleAccordionToggle}
        >
          <IonAccordion value="object">
            <EditorObjectForm
              onOpenFullscreenReference={handleOpenObjectFullscreenReference}
            />
          </IonAccordion>
          {definition.canvasForm && (
            <IonAccordion value="canvas">
              <EditorCanvasForm
                onOpenFullscreenReference={handleOpenCanvasFullscreenReference}
              />
            </IonAccordion>
          )}
        </IonAccordionGroup>
      </ResizablePanel>
    </>
  );
};

export default EditorPanel;
